import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useGeo } from "../lib/locale";
import { TID } from "../constants/catalog";
import { pickHero, pickTiles } from "../constants/appleImages";
import ListingCard from "../components/ListingCard";
import {
  ArrowRight, Recycle, ShieldCheck, Search, Globe, Cpu, Sparkles,
  Leaf, TreePine, Mountain, Wind, ChevronLeft, ChevronRight,
} from "lucide-react";

const EARTH_IMG = "https://images.unsplash.com/photo-1611396000732-f8c9a933424f?auto=format&fit=crop&w=1400&q=80";
const ROTATE_MS = 15 * 60 * 1000; // 15 minutes

// Static metadata for each product world — images injected per render via pickTiles()
const TILE_META = {
  iPhone: { title: "iPhone parts", sub: "From A1660 to the latest. Screens, batteries, MLBs.", accent: "from-[#1d1d1f] to-[#0a0a0d]", fg: "text-white" },
  Mac: { title: "Mac &amp; MacBook", sub: "Top cases, displays, logic boards for every Mac generation.", accent: "from-[#e8e8ed] to-[#f5f5f7]", fg: "text-[#1d1d1f]" },
  iPad: { title: "iPad parts", sub: "Glass, digitisers, batteries, frames &mdash; sourced globally.", accent: "from-[#0071e3] to-[#003a78]", fg: "text-white" },
  AirPods: { title: "AirPods &amp; Audio", sub: "Earbuds, charging cases, speaker modules.", accent: "from-[#f5f5f7] to-[#e8e8ed]", fg: "text-[#1d1d1f]" },
  "Apple Watch": { title: "Apple Watch", sub: "Screens, crowns, bands and complete cases.", accent: "from-[#1d1d1f] to-[#1d1d1f]", fg: "text-white" },
  "Apple TV": { title: "Apple TV &amp; Accessories", sub: "Remotes, power supplies, Siri Remote replacements.", accent: "from-[#0071e3] to-[#0a0a0d]", fg: "text-white" },
};

function ProductMarquee({ tiles }) {
  const ref = useRef(null);
  const scrollBy = (dx) => {
    ref.current?.scrollBy({ left: dx, behavior: "smooth" });
  };
  return (
    <section className="bg-[#f5f5f7] border-y border-black/5">
      <div className="pbp-section pt-16 md:pt-24 pb-2">
        <div className="flex items-end justify-between gap-6 mb-6 md:mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">Categories</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">Six product worlds. One marketplace.</h2>
            <p className="mt-3 text-[#1d1d1f]/75 max-w-xl">
              Find genuine parts for every Apple device. Each listing is geo-tagged and priced in the
              seller&rsquo;s local currency.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button onClick={() => scrollBy(-600)} className="pbp-btn-secondary !px-3 !py-2" aria-label="Scroll left">
              <ChevronLeft className="w-4 h-4" strokeWidth={1.8} />
            </button>
            <button onClick={() => scrollBy(600)} className="pbp-btn-secondary !px-3 !py-2" aria-label="Scroll right">
              <ChevronRight className="w-4 h-4" strokeWidth={1.8} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={ref}
        data-testid="home-product-marquee"
        className="flex gap-5 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-16 px-4 sm:px-6 lg:px-8 scroll-smooth"
        style={{ scrollbarWidth: "thin" }}
      >
        {tiles.map((t) => {
          const meta = TILE_META[t.cat];
          return (
            <Link
              key={t.cat}
              to={`/browse?category=${encodeURIComponent(t.cat)}`}
              className="relative flex-shrink-0 snap-start w-[78vw] sm:w-[58vw] md:w-[44vw] lg:w-[32vw] xl:w-[28vw] aspect-[3/4] rounded-3xl overflow-hidden group"
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${meta.accent}`} />
              <img
                src={t.img}
                alt={t.cat}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
              />
              <div className={`relative z-10 h-full flex flex-col justify-between p-7 md:p-9 ${meta.fg}`}>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] font-semibold opacity-90">{t.cat}</div>
                  <h3 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight" dangerouslySetInnerHTML={{ __html: meta.title }} />
                  <p className="mt-3 max-w-xs text-[15px] opacity-90" dangerouslySetInnerHTML={{ __html: meta.sub }} />
                </div>
                <span className="inline-flex items-center gap-1.5 font-medium opacity-95">
                  Browse {t.cat} <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </span>
              </div>
            </Link>
          );
        })}
        <div className="flex-shrink-0 w-2 md:w-6" />
      </div>
    </section>
  );
}

function MotherEarth() {
  return (
    <section className="bg-white">
      <div className="pbp-section py-20 md:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 pbp-chip mb-5">
            <Leaf className="w-3.5 h-3.5 text-[#34c759]" strokeWidth={1.8} /> Mother Earth
          </div>
          <h2 className="text-4xl md:text-6xl font-semibold tracking-tighter leading-[1.05]">
            Less digging. <span className="text-[#34c759]">More giving back.</span>
          </h2>
          <p className="mt-6 text-lg text-[#1d1d1f]/80 leading-relaxed">
            Every part that finds a second home means one less rare-earth mine dug, one less ocean
            container shipped, one less device crushed for landfill. We&rsquo;re on a mission to be
            carbon neutral across our operations and assisted-transactions by <b>2030</b> &mdash; the
            same milestone Apple set for itself.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-4 gap-5 pbp-stagger">
          {[
            { i: Mountain, v: "1 kg", l: "less ore mined for every smartphone display reused" },
            { i: Wind, v: "55 kg", l: "average CO₂e avoided per refurbished MacBook part" },
            { i: TreePine, v: "0 trees", l: "felled — listings are paper-free, AI-summarised" },
            { i: Recycle, v: "100%", l: "of revenue we make from PartByPart goes back into circular-economy partners" },
          ].map(({ i: Icon, v, l }) => (
            <div key={l} className="rounded-3xl bg-[#f5f5f7] p-7">
              <div className="w-10 h-10 rounded-xl bg-white grid place-items-center text-[#34c759] mb-5">
                <Icon className="w-5 h-5" strokeWidth={1.6} />
              </div>
              <div className="text-3xl font-semibold tracking-tight">{v}</div>
              <div className="mt-2 text-sm text-[#1d1d1f]/75 leading-relaxed">{l}</div>
            </div>
          ))}
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-8 items-center">
          <img src={EARTH_IMG} alt="" className="rounded-3xl w-full h-72 md:h-96 object-cover" />
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">Our pledge</div>
            <h3 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
              Carbon neutral by 2030.
            </h3>
            <ul className="mt-6 space-y-3 text-[#1d1d1f]/85">
              <li className="flex gap-3"><span className="text-[#34c759]">●</span> Every kilo of parts kept in circulation displaces fresh aluminium, cobalt, and rare-earth extraction.</li>
              <li className="flex gap-3"><span className="text-[#34c759]">●</span> Local-first listings reduce cross-border shipping by default — your search begins in your country.</li>
              <li className="flex gap-3"><span className="text-[#34c759]">●</span> We measure scope-3 emissions per transaction and publish a transparency report each quarter.</li>
              <li className="flex gap-3"><span className="text-[#34c759]">●</span> Unsold dead units are routed to certified e-waste recyclers — never to landfill.</li>
            </ul>
            <Link to="/sell" className="mt-7 inline-flex pbp-btn-primary">
              Start your circular impact <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const geo = useGeo();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ listings: 0, users: 0 });
  const [q, setQ] = useState("");
  const [seed, setSeed] = useState(() => Math.floor(Date.now() / ROTATE_MS));
  const heroImg = pickHero(seed);
  const tiles = pickTiles(seed);

  useEffect(() => {
    const t = setInterval(() => setSeed(Math.floor(Date.now() / ROTATE_MS) + 1), ROTATE_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get("/listings", { params: { sort: "newest", limit: 6, country: geo?.country || undefined } })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [geo?.country]);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden pbp-grain">
        <div className="pbp-section pt-16 md:pt-24 pb-12 md:pb-20 relative" data-testid={TID.home.hero}>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 pbp-fade-up">
              <span className="pbp-chip mb-5">
                <Recycle className="w-3.5 h-3.5" strokeWidth={1.8} /> Made in India &middot; Worldwide
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[1.05] text-[#1d1d1f]">
                Give Apple parts<br />a second life.
              </h1>
              <p className="mt-5 max-w-xl text-[#1d1d1f]/80 text-lg leading-relaxed">
                A global peer-to-peer marketplace for genuine Apple parts &mdash; working, partially working,
                or dead. Buy local, sell global, and keep e-waste out of landfills.
              </p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  window.location.href = `/browse?q=${encodeURIComponent(q)}`;
                }}
                className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl"
              >
                <div className="relative flex-1">
                  <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-[#86868b]" strokeWidth={1.5} />
                  <input
                    data-testid={TID.home.search}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Try ‘A2338 logic board’ or ‘iPhone 12 battery’"
                    className="pbp-input pl-11"
                  />
                </div>
                <button data-testid={TID.home.searchBtn} className="pbp-btn-primary px-6">Search</button>
              </form>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/sell" data-testid={TID.home.cta} className="pbp-btn-outline">
                  List a part <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
                </Link>
                <Link to="/browse" data-testid={TID.home.ctaBrowse} className="pbp-btn-ghost">
                  Browse listings
                </Link>
              </div>
              {!!(stats.listings || stats.users) && (
                <div className="mt-8 flex gap-6 text-sm text-[#86868b]">
                  <span><b className="text-[#1d1d1f] text-base">{stats.listings}</b> active listings</span>
                  <span><b className="text-[#1d1d1f] text-base">{stats.users}</b> members</span>
                </div>
              )}
            </div>
            <div className="lg:col-span-5 pbp-fade-up">
              <div className="relative rounded-3xl overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.18)]">
                <img src={heroImg} data-testid="home-hero-img" alt="Apple parts" className="w-full h-[420px] object-cover transition-opacity duration-700" />
                <div className="absolute bottom-4 left-4 right-4 bg-white/85 backdrop-blur-md rounded-2xl p-4 flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-[#0071e3]" strokeWidth={1.8} />
                  <span className="text-sm text-[#1d1d1f]">
                    AI auto-detects the model and part from your photo to prevent listing errors.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT WORLDS - big scrolling band like apple.com */}
      <ProductMarquee tiles={tiles} />

      {/* HOW IT WORKS */}
      <section className="bg-white">
        <div className="pbp-section py-16 md:py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">How it works</div>
              <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">Three steps. Built for trust.</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 pbp-stagger">
            {[
              { icon: Cpu, t: "List with AI assist", d: "Upload 2+ photos. Our AI fills in the model number, part, and condition — and even drafts a realistic description for you." },
              { icon: Globe, t: "Discover locally", d: "Listings default to your country and currency. Filter by distance, condition, year, or seller type." },
              { icon: ShieldCheck, t: "Chat safely", d: "Persistent safety warnings, report system, and human moderation by master admins keep things in check." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="pbp-card p-6 md:p-8">
                <div className="w-11 h-11 rounded-xl bg-[#0071e3]/10 grid place-items-center text-[#0071e3] mb-4">
                  <Icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <div className="text-lg font-semibold tracking-tight">{t}</div>
                <p className="mt-2 text-[#1d1d1f]/75 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MOTHER EARTH / CARBON */}
      <MotherEarth />

      {/* FRESH LISTINGS */}
      <section className="pbp-section py-16 md:py-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">Fresh in {geo?.country || "your region"}</div>
            <h2 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">Newest listings near you</h2>
          </div>
          <Link to="/browse" className="hidden md:inline-flex pbp-btn-ghost">See all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {items.length === 0 ? (
          <div className="pbp-card p-10 text-center text-[#86868b]">
            No listings yet. <Link className="pbp-link ml-1" to="/sell">Be the first to list one.</Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 pbp-stagger">
            {items.map((l) => (
              <ListingCard key={l.listing_id} listing={l} geoCountryCode={geo?.country_code} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
